import { css } from 'aphrodite';
import * as React from 'react';
import { useState } from 'react';
import { ScoreDisplay } from '../../../voice-agent/components/ScoreDisplay';
import type { ScenarioWithCompletion, SkillArea } from '../../../voice-agent/types/scenarios';
import { styleSheet } from './styles';

interface SkillAreaProgressProps {
	skillArea: SkillArea;
	scenarios: ScenarioWithCompletion[];
	onScenarioClick: (scenario: ScenarioWithCompletion) => void;
}

/**
 * SkillAreaProgress displays a collapsible section for a skill area
 * containing all its scenarios with progress indicators
 */
export const SkillAreaProgress: React.FC<SkillAreaProgressProps> = ({ skillArea, scenarios, onScenarioClick }) => {
	const [isExpanded, setIsExpanded] = useState(true);

	// Calculate stats
	const practicedScenarios = scenarios.filter(s => s.attemptCount && s.attemptCount > 0);
	const totalScenarios = scenarios.length;
	const averageScore =
		practicedScenarios.length > 0
			? practicedScenarios
					.filter(s => s.bestScore !== undefined && s.bestScore !== null)
					.reduce((sum, s) => sum + (s.bestScore || 0), 0) / practicedScenarios.length
			: null;

	const formatDate = (dateString: string | undefined) => {
		if (!dateString) return null;
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
	};

	const isCompleted = (scenario: ScenarioWithCompletion) => {
		return scenario.bestScore !== undefined && scenario.bestScore !== null && scenario.bestScore >= 7;
	};

	return (
		<div className={css(styleSheet.skillAreaSection)}>
			<div
				className={css(styleSheet.skillAreaHeader, isExpanded && styleSheet.skillAreaHeaderExpanded)}
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<svg className={css(styleSheet.expandIcon, isExpanded && styleSheet.expandIconRotated)} viewBox='0 0 24 24'>
					<path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z' fill='currentColor' />
				</svg>
				<div className={css(styleSheet.skillAreaInfo)}>
					<div className={css(styleSheet.skillAreaName)}>{skillArea.name}</div>
					<div className={css(styleSheet.skillAreaDescription)}>{skillArea.description}</div>
				</div>
				<div className={css(styleSheet.skillAreaStats)}>
					<div className={css(styleSheet.skillAreaStat)}>
						<div className={css(styleSheet.skillAreaStatLabel)}>Progress</div>
						<div className={css(styleSheet.skillAreaStatValue)}>
							{practicedScenarios.length}/{totalScenarios}
						</div>
					</div>
					{averageScore !== null && (
						<div className={css(styleSheet.skillAreaStat)}>
							<div className={css(styleSheet.skillAreaStatLabel)}>Avg Score</div>
							<div className={css(styleSheet.skillAreaStatValue)}>
								<ScoreDisplay score={averageScore} size='small' />
							</div>
						</div>
					)}
				</div>
			</div>

			{isExpanded && (
				<div className={css(styleSheet.scenariosContainer)}>
					{scenarios.length === 0 ? (
						<div className={css(styleSheet.emptyState)}>No scenarios in this skill area</div>
					) : (
						scenarios.map(scenario => {
							const completed = isCompleted(scenario);
							const hasAttempts = scenario.attemptCount && scenario.attemptCount > 0;

							return (
								<div
									key={scenario.id}
									className={css(styleSheet.scenarioCard)}
									onClick={() => onScenarioClick(scenario)}
								>
									<div
										className={css(
											styleSheet.scenarioIcon,
											completed ? styleSheet.scenarioIconCompleted : styleSheet.scenarioIconNotAttempted
										)}
									>
										{completed ? (
											<svg width='24' height='24' viewBox='0 0 24 24' fill='currentColor'>
												<path d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z' />
											</svg>
										) : (
											<svg width='24' height='24' viewBox='0 0 24 24' fill='currentColor'>
												<circle cx='12' cy='12' r='10' opacity='0.3' />
											</svg>
										)}
									</div>
									<div className={css(styleSheet.scenarioInfo)}>
										<div className={css(styleSheet.scenarioName)}>{scenario.name}</div>
										<div className={css(styleSheet.scenarioMeta)}>
											{hasAttempts ? (
												<>
													{scenario.bestScore !== undefined && scenario.bestScore !== null && (
														<div className={css(styleSheet.scenarioMetaItem)}>
															Best: <ScoreDisplay score={scenario.bestScore} size='small' />
														</div>
													)}
													<div className={css(styleSheet.scenarioMetaItem)}>
														{scenario.attemptCount} {scenario.attemptCount === 1 ? 'attempt' : 'attempts'}
													</div>
													{scenario.lastCompletion?.completedDate && (
														<div className={css(styleSheet.scenarioMetaItem)}>
															Last: {formatDate(scenario.lastCompletion.completedDate)}
														</div>
													)}
												</>
											) : (
												<div className={css(styleSheet.scenarioMetaItem)}>Not attempted</div>
											)}
										</div>
									</div>
								</div>
							);
						})
					)}
				</div>
			)}
		</div>
	);
};
